import { ERROR_TEXT } from '@/theme/themes/colors'
import {
  Box,
  ButtonBase,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded'
import useTranslation from '@/hooks/useTranslation'
import type { GuidedStep } from './guidedSteps'
import type { RailStepStatus } from './guidedStepGaps'

type GuidedStepRailItem = Pick<GuidedStep, 'key' | 'labelKey'>

interface GuidedStepRailProps {
  steps: GuidedStepRailItem[]
  activeStep: number
  visited: Set<number>
  disabled?: Set<number>
  /** Why a step is disabled, by index — shown on hover so the block explains itself. */
  disabledReason?: Record<number, string>
  /**
   * QC round 2, item 4 — each step's marker, by index. Without it a step is
   * ticked once walked past, as before.
   */
  statuses?: RailStepStatus[]
  /** What an unfinished step is missing, by index — shown on hover. */
  incompleteReason?: Record<number, string>
  onSelect: (index: number) => void
}

// The wizard's blocked-reason red, light and dark.
const INCOMPLETE_COLOR = ERROR_TEXT

const CIRCLE = 26

/** Said to screen readers with each step's name: the marker is only drawn. */
const RAIL_STATUS_KEYS: Record<RailStepStatus, string> = {
  current: 'guidedSetupRailStatusCurrent',
  done: 'guidedSetupRailStatusDone',
  incomplete: 'guidedSetupRailStatusIncomplete',
  optional: 'guidedSetupRailStatusOptional',
  upcoming: 'guidedSetupRailStatusUpcoming',
}

/**
 * SCRUM-1032 — the guided setup's left rail.
 *
 * Replaces the stock MUI vertical Stepper, whose default chrome (dot markers,
 * cramped connectors, no active state beyond bold text) read as unfinished next
 * to the rest of the setup UI. This draws the steps as a proper navigation
 * list: numbered markers that become ticks once passed, a continuous connector,
 * and a highlighted row for the step being edited.
 */
const GuidedStepRail = ({
  steps,
  activeStep,
  visited,
  disabled = new Set(),
  disabledReason,
  statuses,
  incompleteReason,
  onSelect,
}: GuidedStepRailProps) => {
  const { t } = useTranslation()
  const progress = ((activeStep + 1) / steps.length) * 100

  return (
    <Stack sx={{ gap: '18px' }}>
      <Stack sx={{ gap: '8px', px: '4px' }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{
              color: 'grey.800',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {t('guidedSetupSteps')}
          </Typography>
          <Typography
            sx={{ color: 'text.main', fontSize: '12px', fontWeight: 600 }}
          >
            {activeStep + 1} / {steps.length}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: 'border.main',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              backgroundColor: 'primary.main',
            },
          }}
        />
      </Stack>

      <Stack sx={{ gap: '2px' }}>
        {steps.map((step, index) => {
          const isActive = index === activeStep
          const status: RailStepStatus =
            statuses?.[index] ??
            (isActive
              ? 'current'
              : visited.has(index) && index < activeStep
                ? 'done'
                : 'upcoming')
          // A tick is the step's data being in, not the step being walked
          // past (QC round 2, item 4).
          const isDone = status === 'done'
          // Red is for a step that stands in the way. An optional step left
          // empty keeps its number and says so on hover instead.
          const isIncomplete = status === 'incomplete'
          const isOptionalEmpty = status === 'optional'
          const isLast = index === steps.length - 1
          const isDisabled = disabled.has(index)
          const statusLabel = t(RAIL_STATUS_KEYS[status])
          const markerColor =
            isActive || isDone ? 'primary.main' : 'transparent'

          const item = (
            <ButtonBase
              key={step.key}
              onClick={() => onSelect(index)}
              disabled={isDisabled}
              data-testid={`rail-step-${step.key}`}
              data-status={status}
              aria-current={isActive ? 'step' : undefined}
              aria-label={t('guidedSetupRailStepAria', {
                step: t(step.labelKey),
                status: statusLabel,
              })}
              sx={{
                position: 'relative',
                justifyContent: 'flex-start',
                textAlign: 'left',
                width: '100%',
                borderRadius: '8px',
                p: '9px 10px',
                gap: '12px',
                transition: 'background-color 120ms ease',
                backgroundColor: isActive
                  ? (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,71,68,0.08)'
                  : 'transparent',
                // A finished step stays solid even when it cannot be opened:
                // Reset is never reopened once done, and fading its tick read
                // as the reset being undone (SCRUM-1032 QC, 30 Oct). The hover
                // reason still says why it cannot be opened.
                opacity: isDisabled && !isDone ? 0.58 : 1,
                '&:hover': {
                  backgroundColor: isActive
                    ? (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.12)'
                          : 'rgba(0,71,68,0.12)'
                    : 'background.overrideDefault',
                },
              }}
            >
              {/* Connector, drawn behind the next marker so the rail reads as
                  one continuous line. */}
              {!isLast && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${10 + CIRCLE / 2 - 1}px`,
                    top: `${9 + CIRCLE}px`,
                    bottom: '-4px',
                    width: '2px',
                    backgroundColor: isDone ? 'primary.main' : 'border.main',
                    opacity: isDone ? 0.5 : 1,
                  }}
                />
              )}

              <Box
                sx={{
                  position: 'relative',
                  flexShrink: 0,
                  width: CIRCLE,
                  height: CIRCLE,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid',
                  borderColor: isIncomplete
                    ? (theme) => INCOMPLETE_COLOR[theme.palette.mode]
                    : isActive || isDone
                      ? 'primary.main'
                      : 'grey.600',
                  backgroundColor: isDone ? markerColor : 'background.default',
                  color: isIncomplete
                    ? (theme) => INCOMPLETE_COLOR[theme.palette.mode]
                    : isDone
                      ? 'background.default'
                      : isActive
                        ? 'primary.main'
                        : 'grey.800',
                }}
              >
                {isDone ? (
                  <CheckIcon sx={{ fontSize: 15 }} />
                ) : isIncomplete ? (
                  <PriorityHighRoundedIcon sx={{ fontSize: 15 }} />
                ) : (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      lineHeight: 1,
                      color: 'inherit',
                    }}
                  >
                    {index + 1}
                  </Typography>
                )}
              </Box>

              <Typography
                sx={{
                  color:
                    isActive || isDone || isIncomplete ? 'text.main' : 'grey.800',
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 500,
                  lineHeight: 1.3,
                }}
              >
                {t(step.labelKey)}
              </Typography>
            </ButtonBase>
          )

          const reason = isDisabled
            ? disabledReason?.[index]
            : isIncomplete || isOptionalEmpty
              ? incompleteReason?.[index]
              : undefined
          if (!reason) return item

          // `span` wrapper: a disabled button fires no pointer events, so the
          // tooltip would never open on the control that needs explaining.
          return (
            <Tooltip
              key={step.key}
              title={reason}
              placement="right"
              arrow
              describeChild
            >
              <Box component="span" sx={{ display: 'block', width: '100%' }}>
                {item}
              </Box>
            </Tooltip>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default GuidedStepRail
